import { getSession, useSession } from 'next-auth/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useIsOnline } from './useIsOnline';

/** Delay between self-repair attempts while the session looks interrupted. */
const RECHECK_INTERVAL_MS = 5000;
/**
 * Cap the self-repair loop. `/api/auth/session` runs NextAuth's `jwt` callback, which
 * rotates the single-use refresh token against a 30s backend grace window — this must
 * never become a steady-state poll.
 */
const MAX_RECHECK_ATTEMPTS = 6;

/**
 * Several components on one page mount this hook (e.g. NoAccess -> NewAppLayout ->
 * AuthGuardProvider), and each would otherwise fire its own recheck. `/api/auth/session`
 * rotates the single-use refresh token, so concurrent calls race each other against the
 * backend's rotation grace window — collapse them into one in-flight request instead.
 */
let inFlightRecheck: ReturnType<typeof getSession> | null = null;
const sharedGetSession = () => {
  if (!inFlightRecheck) {
    inFlightRecheck = getSession().finally(() => {
      inFlightRecheck = null;
    });
  }
  return inFlightRecheck;
};

/** The localStorage key NextAuth's cross-tab "broadcast" writes to. */
const NEXTAUTH_BROADCAST_KEY = 'nextauth.message';

/**
 * Make this tab's SessionProvider re-read the session.
 *
 * `getSession()` alone is not enough: NextAuth 4.x "broadcasts" by writing to
 * localStorage and listening for the `storage` event, and that event never fires in the
 * tab that wrote it. So a recovery fetch from here updates the cookie but leaves the
 * local provider stuck reporting `unauthenticated`.
 *
 * Nor does it heal on its own — with a null cached session the visibilitychange refetch
 * early-returns (react/index.js:506) and the refetchInterval poll is gated behind
 * `if (__NEXTAUTH._session)`. 4.20.1 has no `useSession().update()` either.
 *
 * Dispatching the event NextAuth is already listening for is the one lever that works
 * in-tab. It depends on that localStorage key; if a NextAuth upgrade renames it, recovery
 * quietly stops working (nothing breaks — you just fall back to a manual reload), so the
 * accompanying test asserts the provider actually recovers.
 */
const syncSessionProvider = () => {
  window.dispatchEvent(
    new StorageEvent('storage', {
      key: NEXTAUTH_BROADCAST_KEY,
      newValue: JSON.stringify({ event: 'session', data: { trigger: 'recover' } }),
    }),
  );
};

/**
 * Fetch the session and, if one came back, push it into this tab's SessionProvider.
 *
 * The sync costs a second `/api/auth/session` GET, because the provider does its own
 * fetch when it handles the event. That only happens on the recovery path and only once
 * — the session returning clears `isSessionInterrupted`, which stops everything.
 */
const recoverSession = async () => {
  const session = await sharedGetSession();
  if (session) syncSessionProvider();
  return session;
};

/**
 * A trustworthy read of "is this user signed out?".
 *
 * NextAuth 4.x collapses a FAILED `/api/auth/session` fetch into `session = null`
 * (`client/_utils.js` returns null on any fetch error), which `useSession` then reports
 * as `status: 'unauthenticated'` — indistinguishable from a real sign-out. A laptop
 * switching between a mobile hotspot and wifi therefore looks exactly like a logout, and
 * callers that redirect on `unauthenticated` throw the user out mid-task.
 *
 * Worse, it does not recover on its own: `SessionProvider._getSession` early-returns once
 * its cached session is `null`, so focus refetches stop firing. Only an explicit
 * `getSession()` can un-stick it — which is what the self-repair loop below does.
 *
 * Callers should branch on `isConfirmedSignedOut` (safe to send to /login or a signed-out
 * UI) rather than on `session.status`, and treat `isSessionInterrupted` as a network
 * problem to ride out.
 */
export const useConfirmedSession = () => {
  const session = useSession();
  const { isOnline } = useIsOnline();
  const sessionUserId = session.data?.user.uid;

  // Once we have seen a real session we keep the uid for the lifetime of the mount, so a
  // transient null can never be mistaken for "this was always an anonymous visitor".
  const [confirmedUserId, setConfirmedUserId] = useState<string>();
  useEffect(() => {
    if (sessionUserId) setConfirmedUserId(sessionUserId);
  }, [sessionUserId]);

  const hasBeenAuthenticated = !!confirmedUserId;
  const isSignedOut = session.status === 'unauthenticated';

  /** We had a session and it vanished — a network blip, not a sign-out. */
  const isSessionInterrupted = isSignedOut && hasBeenAuthenticated;
  /** Never authenticated here, and we can reach the server to prove it. */
  const isConfirmedSignedOut = isSignedOut && !hasBeenAuthenticated && isOnline;

  const recheckSession = useCallback(() => recoverSession(), []);

  // Self-repair: NextAuth cannot recover a nulled session on its own, so drive it here.
  const attemptsRef = useRef(0);
  const isAttemptingRef = useRef(false);
  useEffect(() => {
    if (!isSessionInterrupted) {
      attemptsRef.current = 0;
      return;
    }
    let cancelled = false;
    const attempt = async () => {
      // Only spend budget on a retry we actually initiate. An interface switch can
      // fire several `online` events in a row, and each would otherwise burn an
      // attempt while merely joining the request already in flight.
      if (cancelled || isAttemptingRef.current) return;
      if (attemptsRef.current >= MAX_RECHECK_ATTEMPTS) return;
      isAttemptingRef.current = true;
      attemptsRef.current += 1;
      try {
        // A success flips `session.status` back to authenticated, which clears
        // `isSessionInterrupted` and tears this effect down — the stop condition.
        await recoverSession();
      } catch {
        // Transient; the interval will try again.
      } finally {
        isAttemptingRef.current = false;
      }
    };
    const onBackOnline = () => {
      // The OS reporting the network is back is new information, so grant a fresh
      // budget. Without this a long outage exhausts the cap and self-repair never
      // resumes even though the session is still interrupted.
      attemptsRef.current = 0;
      attempt();
    };
    attempt();
    window.addEventListener('online', onBackOnline);
    const interval = setInterval(attempt, RECHECK_INTERVAL_MS);
    return () => {
      cancelled = true;
      window.removeEventListener('online', onBackOnline);
      clearInterval(interval);
    };
  }, [isSessionInterrupted]);

  return {
    session,
    /** Sticky: survives a transient session-fetch failure. */
    userId: sessionUserId ?? confirmedUserId,
    hasBeenAuthenticated,
    isConfirmedSignedOut,
    isSessionInterrupted,
    recheckSession,
  };
};

export default useConfirmedSession;
